<template>
  <div>
    <Popper placement="bottom-start" v-model:open="open" ignoreTriggerClick>
      <template #trigger>
        <div class="input">
          <input
            v-model="search"
            :placeholder="placeholder"
            @input="searchChanged"
            @keydown.up.prevent.stop="selectIndex(selectedIndex - 1)"
            @keydown.down.prevent.stop="selectIndex(selectedIndex + 1)"
            @keydown.enter.prevent.stop="onResultSelect(selectedIndex)"
            @keyup.up.down.enter.prevent.stop
            @keypress.up.down.enter.prevent.stop
            @focus="$event.target.select()"
          />
          <div class="input__suffix" v-if="!search">
            <SearchSvg />
          </div>
          <button
            v-if="search"
            class="input__clear"
            @click="clear"
            :aria-label="$t('general.clear')"
          >
            <CloseCircle16Svg />
          </button>
        </div>
      </template>

      <div class="menu menu--dropdown">
        <ul class="menu__items">
          <li class="menu-item menu-item--dropdown" v-if="loading">
            <div class="item-detail">
              <div class="item-detail__body">
                <div class="item-detail__title typo-small-title">
                  {{ $t('general.loading') }}…
                </div>
              </div>
            </div>
          </li>
          <li class="menu-item menu-item--dropdown" v-if="!loading && error">
            <div class="item-detail">
              <div class="item-detail__body">
                <div class="item-detail__title typo-small-title">
                  <span class="error">{{ error }}</span>
                </div>
              </div>
            </div>
          </li>
          <li class="menu-item menu-item--dropdown" v-if="!loading && !error && results.length === 0">
            <div class="item-detail item-detail--error">
              <div class="item-detail__body">
                <div class="item-detail__title typo-small-title">
                  {{ $t('general.no_results') }}
                </div>
              </div>
            </div>
          </li>
          <template v-if="!loading && !error && results.length > 0">
            <li
              :class="{
                'menu-item menu-item--dropdown': true,
                'menu-item--selected': selectedIndex === index
              }"
              v-for="(result, index) in results"
              :key="result[keyField] || index"
              @click="onResultSelect(index)"
            >
              <slot :result="result" :index="index" :selected="selectedIndex === index">
                <div class="item-detail item-detail--centered item-detail--reversed">
                  <div class="item-detail__body">
                    <div class="item-detail__title typo-small-title">
                      {{ result[titleField] }}
                    </div>
                    <div class="item-detail__info typo-small-title" v-if="infoField && result[infoField]">
                      {{ result[infoField] }}
                    </div>
                  </div>
                  <div class="item-detail__icon">
                    <WrenchSvg />
                  </div>
                </div>
              </slot>
            </li>
          </template>
        </ul>
      </div>
    </Popper>
  </div>
</template>

<script setup>
import { ref, watchEffect } from 'vue';

import debounce from '@/vendor/debounce';

import Popper from '@/components/generic/popper.vue';

import SearchSvg from '@/images/search.svg';
import CloseCircle16Svg from '@/images/close-circle-16.svg';
import WrenchSvg from '@/images/wrench.svg';

// number of milliseconds to debounce the input handler
const DEBOUNCE_MILLISECONDS = 300;

const props = defineProps({
  placeholder: { type: String, default: '' },
  modelValue: { type: [String, Object], default: null },
  resultsFn: { type: Function, required: true },
  keyField: { type: String, default: 'id' },
  titleField: { type: String, default: 'title' },
  infoField: { type: String, default: null },
});
const emit = defineEmits(['update:modelValue']);

const search = ref('');
const open = ref(false);
const loading = ref(false);
const results = ref([]);
const error = ref('');
const selectedIndex = ref(-1);

watchEffect(() => {
  search.value = props.modelValue ?
    props.modelValue[props.titleField] :
    '';
});

function searchChanged() {
  if (search.value) {
    loadResults();
  } else {
    open.value = false;
  }
}

async function loadResults() {
  selectedIndex.value === -1;
  open.value = true;

  await fetchResults();

  if (results.value.length > 0) {
    selectIndex(0);
  }
}
loadResults = debounce(loadResults, DEBOUNCE_MILLISECONDS);

async function fetchResults() {
  loading.value = true;
  error.value = '';
  try {
    results.value = [];
    let result = await props.resultsFn(search.value);
    results.value = result || [];
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
}

function onResultSelect(index) {
  let result = results.value[index];
  search.value = result[props.titleField];
  open.value = false;
  emit('update:modelValue', result);
}

function selectIndex(index) {
  if (results.value.length === 0) {
    selectedIndex.value = -1;
    return;
  }

  if (index < 0) index = results.value.length - index + 1;
  if (index >= results.value.length) index %= results.value.length;
  selectedIndex.value = index;
}

function clear() {
  search.value = '';
  open.value = false;
  results.value = [];
  emit('update:modelValue', null);
}
</script>
